import { useEffect, useRef, useState, type ReactNode } from "react";
import { getPhoneHref, normalizePhone } from "../../store/usersStore";

interface Props {
  phone?: string | null;
  label?: ReactNode;
  className?: string;
  buttonClassName?: string;
  align?: "left" | "right";
}

export default function PhoneActionMenu({
  phone,
  label,
  className = "",
  buttonClassName = "font-semibold text-farm-earth hover:text-farm-brown",
  align = "left",
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const rawPhone = String(phone ?? "").trim();
  const telHref = getPhoneHref(rawPhone);
  const whatsappDigits = normalizePhone(rawPhone).replace(/\D/g, "");
  const display = label ?? rawPhone;

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  if (!rawPhone) {
    return <span className={className}>—</span>;
  }

  return (
    <span ref={rootRef} className={`relative inline-flex max-w-full ${className}`}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className={`inline-flex max-w-full items-center gap-1 truncate transition-colors ${buttonClassName}`}
        title="Telefon seçenekleri"
      >
        <span className="truncate">{display}</span>
        <i className="ri-arrow-down-s-line text-xs" />
      </button>

      {open ? (
        <span
          className={`absolute top-full z-50 mt-2 w-44 rounded-2xl border border-farm-border bg-white p-2 text-xs shadow-xl ${
            align === "right" ? "right-0" : "left-0"
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          {whatsappDigits ? (
            <a
              href={`https://wa.me/${whatsappDigits}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-xl px-3 py-2 font-semibold text-farm-green hover:bg-farm-green-pale"
            >
              <i className="ri-whatsapp-line text-base" />
              WhatsApp
            </a>
          ) : null}

          {telHref ? (
            <a
              href={`tel:${telHref}`}
              className="flex items-center gap-2 rounded-xl px-3 py-2 font-semibold text-farm-earth hover:bg-farm-earth-pale"
            >
              <i className="ri-phone-line text-base" />
              Ara
            </a>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
